import React, { useEffect, useState } from 'react';
import { Printer, RefreshCw, Copy, Check, Trash2, KeyRound, Wifi, WifiOff, MonitorSmartphone, BellRing } from 'lucide-react';
import api from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import { printReceipt } from '../../lib/receipt';

const TEST_ORDER = {
  id: 'test-0000',
  type: 'pickup',
  created_at: new Date().toISOString(),
  customerName: 'Test Print',
  customerPhone: '+1 604 468 4133',
  items: [{ name: 'Chicken Donair Wrap', qty: 1, price: 13.99, modifiers: [
    { groupName: 'Sauce', name: 'Garlic', price: 0 },
    { groupName: 'Extras', name: 'Extra Cheese', price: 1.5 },
  ] }],
  subtotal: 15.49, tax: 0.77, total: 16.26,
  notes: 'Browser test receipt from the Wrappus admin dashboard.',
};

export default function AdminPrinter() {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();

  const load = async () => {
    try {
      const r = await api.get('/admin/print/settings');
      setData(r.data);
    } catch (err) {
      toast({ title: 'Could not load printer settings', description: err?.response?.data?.detail || '' });
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const save = async (patch) => {
    setBusy(true);
    try {
      await api.put('/admin/print/settings', { enabled: data.enabled, copies: data.copies, ...patch });
      await load();
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || '' });
    } finally { setBusy(false); }
  };

  const testCloud = async () => {
    setBusy(true);
    try {
      await api.post('/admin/print/test');
      toast({ title: 'Test receipt queued', description: 'It prints on the next printer poll (a few seconds).' });
      load();
    } catch (err) {
      toast({ title: 'Could not queue test', description: err?.response?.data?.detail || '' });
    } finally { setBusy(false); }
  };

  const testAlert = async () => {
    setBusy(true);
    try {
      const r = await api.post('/admin/print/test-alert');
      toast({ title: r.data.ok ? 'Test alert sent' : 'Email not configured', description: r.data.sentTo });
    } catch (err) {
      toast({ title: 'Could not send test alert', description: err?.response?.data?.detail || '' });
    } finally { setBusy(false); }
  };

  const rotate = async () => {
    if (!window.confirm('Generate a new printer URL? You will need to re-enter it on the printer.')) return;
    setBusy(true);
    try {
      await api.post('/admin/print/rotate-token');
      toast({ title: 'New printer URL generated' });
      load();
    } finally { setBusy(false); }
  };

  const clearQueue = async () => {
    setBusy(true);
    try {
      const r = await api.delete('/admin/print/jobs');
      toast({ title: `Cleared ${r.data.cleared} queued job(s)` });
      load();
    } finally { setBusy(false); }
  };

  const copyUrl = () => {
    navigator.clipboard?.writeText(data.cloudprntUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  if (!data) return <div className="text-gray-500">Loading printer settings…</div>;

  const device = (data.devices || [])[0];
  const online = device && (Date.now() - new Date(device.last_seen).getTime()) < 120000;

  return (
    <div data-testid="admin-printer">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-heading font-bold text-3xl text-[#0F1C2E]">Receipt Printer</h1>
          <p className="text-gray-500 text-sm mt-1">Auto-print every paid order on a thermal printer — no computer required.</p>
        </div>
        <button onClick={load} className="btn-wrappus text-sm" data-testid="printer-refresh"><RefreshCw size={14} /> Refresh</button>
      </div>

      <div className="grid lg:grid-cols-[1fr_320px] gap-5">
        <div className="space-y-5">
          {/* Connection URL */}
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-2"><Printer size={18} className="text-[#ED2E2E]" />
              <h2 className="font-heading font-bold text-lg text-[#0F1C2E]">Your printer URL (CloudPRNT)</h2>
            </div>
            <p className="text-sm text-gray-500 mb-3">Paste this into the printer's CloudPRNT server URL field. Keep it private — anyone with this link can queue receipts.</p>
            <div className="flex flex-wrap gap-2">
              <code className="flex-1 min-w-[240px] bg-[#F7F5F2] border border-gray-200 rounded-xl px-3 py-2.5 text-xs break-all" data-testid="printer-url">{data.cloudprntUrl}</code>
              <button onClick={copyUrl} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#0F1C2E] text-white text-sm font-semibold hover:bg-[#ED2E2E] transition" data-testid="printer-copy-url">
                {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? 'Copied' : 'Copy'}
              </button>
              <button onClick={rotate} disabled={busy} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-gray-300 text-[#0F1C2E] text-sm font-semibold hover:bg-gray-50 transition disabled:opacity-50" data-testid="printer-rotate">
                <KeyRound size={14} /> New URL
              </button>
            </div>
          </div>

          {/* Setup steps */}
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <h2 className="font-heading font-bold text-lg text-[#0F1C2E] mb-3">How to connect a thermal printer</h2>
            <div className="space-y-4 text-sm text-gray-700">
              <div>
                <div className="font-bold text-[#0F1C2E] mb-1">Option A — Cloud printer (recommended, no computer)</div>
                <ol className="list-decimal list-inside space-y-1.5">
                  <li>Buy a Star Micronics CloudPRNT printer — <b>Star TSP143IV UE</b> (LAN) or <b>TSP143IIIW</b> (Wi-Fi), 80 mm paper. Epson TM-m30III with Server Direct Print also works.</li>
                  <li>Plug in power, load an 80 mm roll, connect the printer to your shop's router (Ethernet cable or Wi-Fi setup card).</li>
                  <li>Hold the FEED button while switching the printer on to print a self-test — note the printer's IP address.</li>
                  <li>On any device on the same Wi-Fi, open a browser and go to <code className="bg-gray-100 px-1 rounded">http://PRINTER-IP/</code> (Star Printer Configuration). Login is usually <code className="bg-gray-100 px-1 rounded">root</code> / <code className="bg-gray-100 px-1 rounded">public</code>.</li>
                  <li>Open <b>CloudPRNT</b> settings → set <b>Server URL</b> to the URL above → <b>Polling interval</b> 5 seconds → enable CloudPRNT → Save &amp; restart.</li>
                  <li>Come back here: the status panel turns green once the printer starts polling. Hit <b>Send test receipt</b>.</li>
                </ol>
              </div>
              <div>
                <div className="font-bold text-[#0F1C2E] mb-1">Option B — Browser print backup (any USB printer)</div>
                <ol className="list-decimal list-inside space-y-1.5">
                  <li>Connect any 80 mm thermal printer (USB or network) to a tablet/PC and install its driver.</li>
                  <li>Set the paper size to 80 mm roll in the printer's driver settings, margins minimal.</li>
                  <li>Keep <b>Kitchen Board</b> open on that device and press <b>Print</b> on an order card — or enable <b>Auto-print new orders</b> there.</li>
                  <li>In the browser print dialog, pick the thermal printer and tick "Background graphics" off for the fastest print.</li>
                </ol>
              </div>
            </div>
          </div>

          {/* Recent jobs */}
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-heading font-bold text-lg text-[#0F1C2E]">Print queue</h2>
              <button onClick={clearQueue} disabled={busy} className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-[#ED2E2E]" data-testid="printer-clear-queue"><Trash2 size={14} /> Clear pending</button>
            </div>
            {(data.jobs || []).length === 0 ? (
              <div className="text-sm text-gray-400">No print jobs yet.</div>
            ) : (
              <div className="divide-y divide-gray-100">
                {data.jobs.map((j) => (
                  <div key={j.id} className="py-2 flex items-center justify-between text-sm">
                    <div>
                      <span className="font-mono text-xs text-gray-500 mr-2">#{(j.order_id || j.id).slice(0, 8).toUpperCase()}</span>
                      <span className="text-gray-700">{j.kind}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-400">{new Date(j.created_at).toLocaleTimeString()}</span>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${j.status === 'printed' ? 'bg-emerald-50 text-emerald-700' : j.status === 'pending' ? 'bg-amber-50 text-amber-700' : j.status === 'sent' ? 'bg-blue-50 text-blue-700' : 'bg-red-50 text-red-700'}`}>{j.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Status sidebar */}
        <div className="space-y-4">
          <div className={`rounded-2xl p-5 shadow-sm ${online ? 'bg-emerald-50 border border-emerald-200' : 'bg-white'}`} data-testid="printer-status">
            <div className="flex items-center gap-2 mb-2">
              {online ? <Wifi size={18} className="text-emerald-600" /> : <WifiOff size={18} className="text-gray-400" />}
              <span className="font-heading font-bold text-[#0F1C2E]">{online ? 'Printer online' : 'No printer connected'}</span>
            </div>
            {device ? (
              <div className="text-xs text-gray-600 space-y-1">
                <div>MAC: <span className="font-mono">{device.mac}</span></div>
                {device.status && <div>Status: {device.status}</div>}
                <div>Last poll: {new Date(device.last_seen).toLocaleTimeString()}</div>
              </div>
            ) : (
              <p className="text-xs text-gray-500">Once the printer polls this server it will show up here automatically.</p>
            )}
            <div className="mt-3 text-sm">Pending jobs: <b data-testid="printer-pending">{data.pending}</b></div>
          </div>

          <div className="bg-white rounded-2xl p-5 shadow-sm space-y-3" data-testid="printer-alerts-card">
            <div className="flex items-center gap-2">
              <BellRing size={18} className={data.alertState === 'ok' ? 'text-emerald-600' : 'text-[#ED2E2E]'} />
              <span className="font-heading font-bold text-[#0F1C2E]">Health alerts</span>
            </div>
            <p className="text-xs text-gray-500">
              We email you if the printer stops checking in for {data.offlineAfterMinutes || 5} minutes, or reports paper-out / cover-open / an error — and again when it recovers.
            </p>
            <div className="text-xs text-gray-600">Sent to: <span className="font-semibold break-all">{data.alertEmails || '—'}</span></div>
            <div className="text-xs">Current state:{' '}
              <span className={`font-semibold px-2 py-0.5 rounded-full ${data.alertState === 'ok' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`} data-testid="printer-alert-state">
                {data.alertState === 'ok' ? 'healthy' : data.alertState}
              </span>
            </div>
            <button onClick={testAlert} disabled={busy} className="inline-flex w-full items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-gray-300 text-[#0F1C2E] text-sm font-semibold hover:bg-gray-50 transition disabled:opacity-50" data-testid="printer-test-alert">
              <BellRing size={14} /> Send test alert email
            </button>
          </div>

          <div className="bg-white rounded-2xl p-5 shadow-sm space-y-3">
            <label className="flex items-center justify-between text-sm">
              <span className="font-semibold text-[#0F1C2E]">Auto-print paid orders</span>
              <input type="checkbox" checked={!!data.enabled} onChange={(e) => { setData({ ...data, enabled: e.target.checked }); save({ enabled: e.target.checked }); }} className="accent-[#ED2E2E] w-4 h-4" data-testid="printer-enabled-toggle" />
            </label>
            <label className="block text-sm">
              <span className="font-semibold text-[#0F1C2E]">Copies per order</span>
              <select value={data.copies} onChange={(e) => { const c = Number(e.target.value); setData({ ...data, copies: c }); save({ copies: c }); }} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2" data-testid="printer-copies">
                {[1, 2, 3].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
            <button onClick={testCloud} disabled={busy} className="btn-wrappus text-sm w-full justify-center disabled:opacity-50" data-testid="printer-test-cloud"><Printer size={14} /> Send test receipt</button>
            <button onClick={() => printReceipt(TEST_ORDER)} className="inline-flex w-full items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-gray-300 text-[#0F1C2E] text-sm font-semibold hover:bg-gray-50 transition" data-testid="printer-test-browser"><MonitorSmartphone size={14} /> Browser test print</button>
          </div>
        </div>
      </div>
    </div>
  );
}
